/**
 * OrderDetails Drawer (Uber Eats Style)
 *
 * Professional vertical viewer for a single order.
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  X,
  User,
  Phone,
  MapPin,
  Clock,
  CreditCard,
  RefreshCw,
  Check,
  Package,
  Bike,
  ShoppingBag,
  Info,
} from "lucide-react";
import dayjs from "dayjs";
import {
  Order,
  OrderDish,
  OrderStatus,
  User as UserType,
  DeliveryMethod,
} from "@tasty/api";
import { OrderClient, OrderDishClient, UserClient } from "@tasty/api/client";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { cn, formatPrice, formatOrderId } from "@/lib/utils";
import getFees from "@/utils/get-fees";

interface OrderDetailsDrawerProps {
  orderId: string;
  onClose: () => void;
  onStatusChanged: (order: Order) => void;
}

const STATUS_CONFIG: Record<
  OrderStatus,
  { label: string; color: string; bgColor: string }
> = {
  [OrderStatus.PENDING]: { label: "Ausstehend", color: "text-slate-700", bgColor: "bg-slate-100" },
  [OrderStatus.NEW]: { label: "Neu", color: "text-blue-700", bgColor: "bg-blue-100" },
  [OrderStatus.ACCEPTED]: { label: "Angenommen", color: "text-teal-700", bgColor: "bg-teal-100" },
  [OrderStatus.COOKING]: { label: "In Zubereitung", color: "text-orange-700", bgColor: "bg-orange-100" },
  [OrderStatus.READY_FOR_PICKUP]: { label: "Bereit", color: "text-green-700", bgColor: "bg-green-100" },
  [OrderStatus.PICKED_UP]: { label: "Unterwegs", color: "text-purple-700", bgColor: "bg-purple-100" },
  [OrderStatus.COMPLETED]: { label: "Abgeschlossen", color: "text-gray-700", bgColor: "bg-gray-100" },
  [OrderStatus.DECLINED_BY_RESTAURANT]: { label: "Abgelehnt", color: "text-red-700", bgColor: "bg-red-100" },
};

export function OrderDetailsDrawer({
  orderId,
  onClose,
  onStatusChanged,
}: OrderDetailsDrawerProps) {
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [order, setOrder] = useState<Order | null>(null);
  const [customer, setCustomer] = useState<UserType | null>(null);
  const [orderDishes, setOrderDishes] = useState<OrderDish[]>([]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const theOrder = await OrderClient.queryItem(orderId);
      if (!theOrder) {
        toast.error("Bestellung nicht gefunden");
        onClose();
        return;
      }
      setOrder(theOrder);

      const dishes = await OrderDishClient.queryListByOrderID({ orderID: orderId });
      setOrderDishes(dishes || []);

      if (theOrder.userID) {
        const user = await UserClient.queryItem({ id: theOrder.userID });
        setCustomer(user);
      }
    } catch (error) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, [orderId, onClose]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateStatus = async (newStatus: OrderStatus) => {
    if (!order) return;
    setUpdating(true);
    try {
      const updated = await OrderClient.updateItem({ id: order.id, status: newStatus });
      if (updated) {
        setOrder(updated);
        onStatusChanged(updated);
        toast.success("Status aktualisiert");
      }
    } catch (error) {
      toast.error("Aktualisierung fehlgeschlagen");
    } finally {
      setUpdating(false);
    }
  };

  const statusConfig = order ? STATUS_CONFIG[order.status] || STATUS_CONFIG.NEW : STATUS_CONFIG.NEW;
  const fees = order ? getFees(order.createdAt, order.deliveryMethod, order.deliveryProvider) : { deliveryFee: 0 };

  if (loading) {
    return (
      <SheetContent className="flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </SheetContent>
    );
  }

  if (!order) return null;

  return (
    <SheetContent className="flex flex-col h-full sm:max-w-xl p-0 overflow-hidden border-l shadow-2xl">
      <SheetHeader className="p-6 border-b shrink-0 bg-muted/5">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <SheetTitle className="truncate text-xl font-bold tracking-tight">
              Bestellung #{orderId.slice(-4).toUpperCase()}
            </SheetTitle>
            <SheetDescription className="text-xs font-medium uppercase tracking-widest text-muted-foreground/60 mt-1">
              {dayjs(order.createdAt).format("DD.MM.YYYY HH:mm")}
            </SheetDescription>
          </div>
          <Badge className={cn("h-7 px-3 font-bold shadow-sm", statusConfig.bgColor, statusConfig.color)}>
            {statusConfig.label}
          </Badge>
        </div>
      </SheetHeader>

      <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-muted space-y-10">
        <div className="space-y-8 pb-12">
          {/* Order Note */}
          {order.note && (
            <section className="p-4 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800/50 rounded-2xl">
              <div className="flex items-center gap-2 mb-1.5">
                <Info className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                <span className="text-[11px] font-bold uppercase tracking-widest text-orange-800 dark:text-orange-300">Hinweis</span>
              </div>
              <p className="text-sm text-orange-700 dark:text-orange-200 font-medium leading-relaxed italic">"{order.note}"</p>
            </section>
          )}

          {/* Customer */}
          <section className="space-y-4">
            <h3 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80 ml-1">Kundeninformationen</h3>
            <div className="p-5 rounded-2xl border border-muted-foreground/10 bg-muted/5 space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground"><User className="h-4 w-4" /></div>
                <span className="text-sm font-bold">{customer?.name || "Gast-Bestellung"}</span>
              </div>
              {customer?.phone && (
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground"><Phone className="h-4 w-4" /></div>
                  <span className="text-sm font-medium">{customer.phone}</span>
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground"><MapPin className="h-4 w-4" /></div>
                <span className="text-sm font-medium leading-tight">
                  {customer?.address || (customer?.addressData as any)?.formattedAddress || "Keine Adresse"}
                </span>
              </div>
            </div>
          </section>

          {/* Items */}
          <section className="space-y-4">
            <h3 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80 ml-1">Positionen</h3>
            <div className="rounded-2xl border border-muted-foreground/10 overflow-hidden bg-background shadow-sm">
              <div className="divide-y divide-muted-foreground/5">
                {orderDishes.map((dish) => (
                  <div key={dish.id} className="p-4 flex justify-between gap-4 group">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-sm">{dish.quantity}x</span>
                        <span className="font-bold text-sm truncate">{dish.Dish?.name || "Unbekannt"}</span>
                      </div>
                      {dish.note && (
                        <p className="text-[11px] text-orange-600 font-medium mt-1 ml-6 italic">💬 {dish.note}</p>
                      )}
                    </div>
                    <span className="text-sm font-black tabular-nums">{formatPrice((dish.Dish?.price || 0) * dish.quantity)}</span>
                  </div>
                ))}
              </div>
              <div className="bg-muted/5 p-4 border-t border-muted-foreground/10 space-y-2">
                <div className="flex justify-between text-xs font-medium text-muted-foreground">
                  <span>Zwischensumme</span>
                  <span className="tabular-nums">{formatPrice(order.total)}</span>
                </div>
                {order.deliveryMethod === DeliveryMethod.DELIVERY && (
                  <div className="flex justify-between text-xs font-medium text-muted-foreground">
                    <span>Liefergebühr</span>
                    <span className="tabular-nums">{formatPrice(fees.deliveryFee || 0)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 text-base font-black">
                  <span>Gesamt</span>
                  <span className="text-primary">{formatPrice(order.total + (fees.deliveryFee || 0))}</span>
                </div>
              </div>
            </div>
          </section>

          {/* Actions */}
          <section className="pt-6 space-y-3">
            {order.status === OrderStatus.NEW && (
              <div className="grid grid-cols-2 gap-3">
                <Button variant="ghost" className="h-12 rounded-xl text-destructive font-bold uppercase tracking-widest text-[10px] bg-destructive/5 hover:bg-destructive/10" disabled={updating} onClick={() => updateStatus(OrderStatus.DECLINED_BY_RESTAURANT)}>Ablehnen</Button>
                <Button className="h-12 rounded-xl bg-success text-success-foreground hover:bg-success/90 font-bold uppercase tracking-widest text-[10px]" disabled={updating} onClick={() => updateStatus(OrderStatus.COOKING)}>Annehmen</Button>
              </div>
            )}
            {order.status === OrderStatus.COOKING && (
              <Button className="w-full h-14 rounded-2xl bg-success text-success-foreground hover:bg-success/90 font-black uppercase tracking-widest text-xs shadow-lg shadow-success/20" disabled={updating} onClick={() => updateStatus(OrderStatus.READY_FOR_PICKUP)}>
                {updating ? <RefreshCw className="h-5 w-5 animate-spin" /> : <Package className="h-5 w-5 mr-2" />}
                Bereit zur Abholung
              </Button>
            )}
            {order.status === OrderStatus.READY_FOR_PICKUP && order.deliveryMethod === DeliveryMethod.PICKUP && (
              <Button className="w-full h-14 rounded-2xl bg-success text-success-foreground hover:bg-success/90 font-black uppercase tracking-widest text-xs shadow-lg shadow-success/20" disabled={updating} onClick={() => updateStatus(OrderStatus.COMPLETED)}>
                <Check className="h-5 w-5 mr-2" /> Abgeholt & Fertig
              </Button>
            )}
          </section>
        </div>
      </div>
    </SheetContent>
  );
}

